// components/playbook-preview-card.js — LIVE PREVIEW CARD
// ═══════════════════════════════════════════════════════════
// Renders a proposed playbook item as a card so users see
// exactly what they'll save. Used in features/playbook-create-chat.js
// as the right-side preview.
//
// Fields are click-to-edit inline. Any field edit flows back up to
// the parent via onChange(nextItem). Title, tags, and structured
// content fields all edit in place.
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { PLAYBOOK_CATEGORIES, getCategoryForType } from '../hooks/use-playbook-items';
import { shapeForSubtype, rationaleForSubtype, SUBTYPE_REGISTRY } from '../lib/playbook-schemas';

const TYPE_LABELS = {
  mental_model: 'Mental Model', framework: 'Framework', heuristic: 'Heuristic',
  principle: 'Principle', decision_matrix: 'Decision Matrix', snippet: 'Snippet', prompt: 'Prompt',
  template: 'Template', process: 'Process', sop: 'SOP', workflow: 'Workflow',
  sequence: 'Sequence', checklist: 'Checklist', routine: 'Routine', algorithm: 'Algorithm',
  pipeline: 'Pipeline', protocol: 'Protocol', method: 'Method', function: 'Function', lifecycle: 'Lifecycle',
  cheatsheet: 'Cheatsheet', diagnostic: 'Diagnostic', rubric: 'Rubric', benchmark: 'Benchmark',
  anti_pattern: 'Anti-Pattern', trigger: 'Trigger', exit_criteria: 'Exit Criteria',
  scenario: 'Scenario', calibration: 'Calibration',
  structure: 'Structure', taxonomy: 'Taxonomy', layout: 'Layout', constraint_set: 'Constraints',
  flowchart: 'Flowchart', formula: 'Formula', blueprint: 'Blueprint',
};

export default function PlaybookPreviewCard({ item, onChange, pending = false }) {
  const [showTypeSwitcher, setShowTypeSwitcher] = useState(false);

  if (!item || pending) {
    return (
      <div style={{ ...emptyStyle }}>
        <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#555', marginBottom: 12 }}>
          Preview
        </div>
        <div style={{ fontSize: 13, fontStyle: 'italic', color: '#666', fontFamily: 'Georgia, serif', textAlign: 'center', lineHeight: 1.6 }}>
          {pending
            ? 'Working on it…'
            : 'Your card will appear here as we go.'}
        </div>
      </div>
    );
  }

  const category = getCategoryForType(item.item_type);
  const accent = PLAYBOOK_CATEGORIES[category]?.color || '#888';
  const typeLabel = TYPE_LABELS[item.item_type] || item.item_type;
  const rationale = rationaleForSubtype(item.item_type);

  function updateField(key, value) {
    onChange?.({ ...item, [key]: value });
  }

  function updateContent(patch) {
    onChange?.({ ...item, content: { ...(item.content || {}), ...patch } });
  }

  function switchType(newType) {
    onChange?.({ ...item, item_type: newType });
    setShowTypeSwitcher(false);
  }

  return (
    <div style={cardStyle(accent)}>
      {/* Accent top stripe */}
      <div style={{ height: 2, background: `linear-gradient(90deg, ${accent}, transparent)` }} />

      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Type pill + rationale */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowTypeSwitcher(v => !v)}
              style={typePillStyle(accent)}
              title="Click to switch type"
            >
              <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: accent, marginRight: 6 }} />
              {typeLabel}
              <span style={{ marginLeft: 6, opacity: 0.5, fontSize: 8 }}>▾</span>
            </button>
            {showTypeSwitcher && (
              <TypeSwitcher currentType={item.item_type} onPick={switchType} onClose={() => setShowTypeSwitcher(false)} />
            )}
          </div>
        </div>

        {/* Inline rationale — teaches what this type means */}
        {rationale && (
          <div style={{ fontSize: 10, color: '#666', fontStyle: 'italic', fontFamily: 'Georgia, serif', marginTop: -4 }}>
            {rationale}
          </div>
        )}

        {/* Title — inline editable */}
        <EditableText
          value={item.title || ''}
          onChange={v => updateField('title', v)}
          placeholder="Untitled"
          style={{ fontSize: 15, color: '#f0f0f0', fontWeight: 'bold', lineHeight: 1.35, fontFamily: 'Georgia, serif' }}
        />

        {/* Structured body — renders per shape */}
        <ContentRenderer item={item} accent={accent} onContentChange={updateContent} />

        {/* Tags */}
        {Array.isArray(item.tags) && item.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {item.tags.map(t => (
              <span key={t} style={tagStyle}>{t}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Content renderer per shape ─────────────────────────────
function ContentRenderer({ item, accent, onContentChange }) {
  const shape = shapeForSubtype(item.item_type);
  const c = item.content || {};

  // Purpose / core_concept / statement / rule / what_scored / prompt_text — the lead line
  const leadKey = shape.bulletKeys?.[0];
  const leadValue = leadKey ? c[leadKey] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {leadValue && typeof leadValue === 'string' && (
        <EditableText
          value={leadValue}
          onChange={v => onContentChange({ [leadKey]: v })}
          placeholder={leadKey.replace(/_/g, ' ')}
          style={{ fontSize: 12, color: '#ccc', lineHeight: 1.6, fontStyle: 'italic', fontFamily: 'Georgia, serif' }}
        />
      )}

      {/* Steps / items / phases / questions / criteria / blocks / parts / categories */}
      <ListFields item={item} accent={accent} onContentChange={onContentChange} />

      {/* Secondary fields like how_it_applies, example, why_it_matters, when_to_break_it, counter_case, use_case, example_output, when_to_use */}
      <SecondaryFields item={item} onContentChange={onContentChange} />
    </div>
  );
}

function ListFields({ item, accent, onContentChange }) {
  const c = item.content || {};

  if (Array.isArray(c.steps) && c.steps.length > 0) {
    return (
      <ol style={listStyle}>
        {c.steps.map((s, i) => (
          <li key={i} style={listItemStyle(accent)}>
            <EditableText
              value={s?.description || ''}
              onChange={v => {
                const next = [...c.steps];
                next[i] = { ...(next[i] || {}), description: v };
                onContentChange({ steps: next });
              }}
              placeholder="Step…"
              style={stepTextStyle}
            />
          </li>
        ))}
      </ol>
    );
  }

  if (Array.isArray(c.items) && c.items.length > 0) {
    return (
      <ul style={listStyle}>
        {c.items.map((it, i) => (
          <li key={i} style={listItemStyle(accent, '—')}>
            <EditableText
              value={it?.check || ''}
              onChange={v => {
                const next = [...c.items];
                next[i] = { ...(next[i] || {}), check: v };
                onContentChange({ items: next });
              }}
              placeholder="Item…"
              style={stepTextStyle}
            />
          </li>
        ))}
      </ul>
    );
  }

  if (Array.isArray(c.phases) && c.phases.length > 0) {
    return (
      <div style={listStyle}>
        {c.phases.map((p, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ color: accent, fontSize: 11, letterSpacing: '0.05em', fontFamily: 'Georgia, serif' }}>
              {p?.name || `Phase ${i + 1}`}
            </div>
            {p?.description && (
              <div style={{ color: '#aaa', fontSize: 11, lineHeight: 1.5, fontFamily: 'Georgia, serif' }}>{p.description}</div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(c.questions) && c.questions.length > 0) {
    return (
      <ul style={listStyle}>
        {c.questions.map((q, i) => (
          <li key={i} style={listItemStyle(accent, '?')}>
            <span style={stepTextStyle}>{q}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (Array.isArray(c.criteria) && c.criteria.length > 0) {
    return (
      <div style={listStyle}>
        {c.criteria.map((cr, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ color: accent, fontSize: 11, fontFamily: 'Georgia, serif' }}>
              {cr?.name}{cr?.weight != null ? ` · ${cr.weight}` : ''}
            </div>
            {cr?.description && (
              <div style={{ color: '#aaa', fontSize: 11, lineHeight: 1.5, fontFamily: 'Georgia, serif' }}>{cr.description}</div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(c.blocks) && c.blocks.length > 0) {
    return (
      <div style={listStyle}>
        {c.blocks.map((b, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ color: accent, fontSize: 11, fontFamily: 'Georgia, serif' }}>{b?.heading}</div>
            {b?.body && <div style={{ color: '#aaa', fontSize: 11, lineHeight: 1.5, fontFamily: 'Georgia, serif' }}>{b.body}</div>}
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(c.parts) && c.parts.length > 0) {
    return (
      <ul style={listStyle}>
        {c.parts.map((p, i) => (
          <li key={i} style={listItemStyle(accent, '◦')}>
            <span style={stepTextStyle}>
              <span style={{ color: accent }}>{p?.name}</span>
              {p?.role ? ` — ${p.role}` : ''}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  if (Array.isArray(c.categories) && c.categories.length > 0) {
    return (
      <div style={listStyle}>
        {c.categories.map((cat, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ color: accent, fontSize: 11, fontFamily: 'Georgia, serif' }}>{cat?.name}</div>
            {cat?.definition && (
              <div style={{ color: '#aaa', fontSize: 11, lineHeight: 1.5, fontFamily: 'Georgia, serif' }}>{cat.definition}</div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(c.sections) && c.sections.length > 0) {
    return (
      <div style={listStyle}>
        {c.sections.map((s, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ color: accent, fontSize: 11, fontFamily: 'Georgia, serif', marginBottom: 2 }}>{s?.heading}</div>
            {Array.isArray(s?.rows) && s.rows.map((r, j) => (
              <div key={j} style={{ color: '#aaa', fontSize: 11, fontFamily: 'Georgia, serif', lineHeight: 1.6 }}>
                <span style={{ color: '#888' }}>{r.key}</span>
                {r.value ? ` — ${r.value}` : ''}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Snippet — just the text
  if (typeof c.text === 'string' && c.text.trim()) {
    return (
      <div style={{ color: '#ccc', fontSize: 12, lineHeight: 1.65, fontFamily: 'Georgia, serif', whiteSpace: 'pre-wrap' }}>
        {c.text}
      </div>
    );
  }

  return null;
}

function SecondaryFields({ item, onContentChange }) {
  const c = item.content || {};
  const rows = [];

  // Map known secondary fields by label
  const FIELDS = [
    ['how_it_applies', 'How it applies'],
    ['example', 'Example'],
    ['why_it_matters', 'Why it matters'],
    ['when_to_break_it', 'When to break it'],
    ['when_to_use', 'When to use'],
    ['counter_case', 'Counter-case'],
    ['use_case', 'Use case'],
    ['example_output', 'Example output'],
  ];

  for (const [key, label] of FIELDS) {
    const val = c[key];
    if (typeof val === 'string' && val.trim()) rows.push({ key, label, val });
  }

  if (rows.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4, paddingTop: 6, borderTop: '1px solid #141414' }}>
      {rows.map(r => (
        <div key={r.key}>
          <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555', marginBottom: 2 }}>
            {r.label}
          </div>
          <div style={{ color: '#aaa', fontSize: 11, fontFamily: 'Georgia, serif', lineHeight: 1.5 }}>
            {r.val}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Click-to-edit text field ──────────────────────────────
function EditableText({ value, onChange, placeholder, style }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  if (!editing) {
    return (
      <div
        onClick={() => { setDraft(value || ''); setEditing(true); }}
        style={{ ...style, cursor: 'text', padding: '2px 0' }}
      >
        {value || <span style={{ color: '#444', fontStyle: 'italic' }}>{placeholder}</span>}
      </div>
    );
  }

  return (
    <input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { onChange(draft); setEditing(false); }}
      onKeyDown={e => {
        if (e.key === 'Enter') { onChange(draft); setEditing(false); }
        if (e.key === 'Escape') setEditing(false);
      }}
      placeholder={placeholder}
      style={{
        ...style,
        width: '100%',
        background: '#141414',
        border: '1px solid #2a2a2a',
        borderRadius: 4,
        padding: '4px 8px',
        outline: 'none',
      }}
    />
  );
}

// ── Type switcher popover ──────────────────────────────────
function TypeSwitcher({ currentType, onPick, onClose }) {
  const category = getCategoryForType(currentType);
  const siblings = PLAYBOOK_CATEGORIES[category]?.types || [];
  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, marginTop: 4,
      background: '#141414', border: '1px solid #2a2a2a', borderRadius: 6,
      padding: 4, zIndex: 30, minWidth: 160, maxHeight: 200, overflowY: 'auto',
      boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
    }}>
      <div style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#555', padding: '6px 8px 4px' }}>
        Switch type
      </div>
      {siblings.map(t => (
        <button
          key={t}
          onClick={() => onPick(t)}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            background: t === currentType ? '#1e1e2a' : 'transparent',
            border: 'none', color: t === currentType ? '#c8a96e' : '#aaa',
            fontSize: 11, fontFamily: 'Georgia, serif',
            padding: '5px 8px', borderRadius: 3, cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#1e1e2a'}
          onMouseLeave={e => e.currentTarget.style.background = t === currentType ? '#1e1e2a' : 'transparent'}
        >
          {TYPE_LABELS[t] || t}
        </button>
      ))}
      <button
        onClick={onClose}
        style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: '#555', fontSize: 10, padding: '6px 8px', cursor: 'pointer', borderTop: '1px solid #1e1e2a', marginTop: 2 }}
      >
        Close
      </button>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────
function cardStyle(accent) {
  return {
    background: '#07070a',
    border: `1px solid ${accent}33`,
    borderRadius: 8,
    overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    boxShadow: `0 4px 16px rgba(0,0,0,0.4)`,
  };
}

function typePillStyle(accent) {
  return {
    display: 'inline-flex', alignItems: 'center',
    fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: accent, background: `${accent}14`,
    border: `1px solid ${accent}33`,
    padding: '3px 9px', borderRadius: 20,
    fontFamily: 'Georgia, serif', cursor: 'pointer',
  };
}

const tagStyle = {
  fontSize: 10, color: '#666',
  background: '#141414', border: '1px solid #272736',
  padding: '2px 8px', borderRadius: 12,
  fontFamily: 'Georgia, serif',
};

const listStyle = {
  margin: 0, paddingLeft: 0, listStyle: 'none',
  display: 'flex', flexDirection: 'column', gap: 4,
};

function listItemStyle(accent, marker = null) {
  return {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    color: '#ccc', fontSize: 12, fontFamily: 'Georgia, serif',
    lineHeight: 1.5,
    ...(marker ? {
      position: 'relative',
      paddingLeft: 18,
    } : {}),
    '&::before': marker ? {
      content: `"${marker}"`,
      position: 'absolute', left: 0, color: accent,
    } : undefined,
  };
}

const stepTextStyle = {
  flex: 1, color: '#ccc', fontSize: 12, fontFamily: 'Georgia, serif', lineHeight: 1.5,
};

const emptyStyle = {
  padding: '40px 24px',
  border: '1px dashed #222',
  borderRadius: 8,
  background: 'rgba(200,169,110,0.02)',
  minHeight: 180,
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
};
